import React, { useMemo, useRef, useState, useEffect } from 'react';
import classNames from 'classnames';
import { Overlay, Portal } from 'ncoded-component-library';
import usePositioning from './hooks/usePositioning';
import useResizeReposition from './hooks/useResizeReposition';
import { XOR } from 'types';

import './ResolvePopup.styles.scss';

type OpenOnHover = {
  onHover: true;
  customOnHover?: () => void;
};

type OpenOnState = {
  open: boolean;
};

type ResolvePopupProps = {
  className?: string;
  portalClassName?: string;
  popupContent: React.ReactNode;
  contentClassName?: string;
  setRef?: React.MutableRefObject<HTMLElement>;
  style?: React.CSSProperties;
  preferUpwards?: boolean;
} & XOR<OpenOnHover, OpenOnState>;

const ResolvePopup: React.FC<ResolvePopupProps> = (props) => {
  const {
    children,
    className,
    popupContent,
    portalClassName,
    contentClassName,
    setRef,
    style,
    open,
    onHover,
    preferUpwards,
    customOnHover,
  } = props;
  const [isUpwards, setIsUpwards] = useState<boolean>(false);
  const [isBackwards, setIsBackwards] = useState<boolean>(false);

  const popupRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLDivElement>(null);

  const [isOpen, setIsOpen] = useState(false);

  usePositioning({
    triggerRef,
    contentRef,
    isOpen,
    preferUpwards,
    renderAsPortal: true,
    dropdownRef: popupRef,
    setIsUpwards,
    setIsBackwards,
  });

  useResizeReposition({
    triggerRef,
    contentRef,
    isUpwards,
    isBackwards,
    renderAsPortal: true,
    dropdownRef: popupRef,
  });

  const classes = classNames(
    'resolve-popup',
    {
      'resolve-popup--open': isOpen,
      'resolve-popup--upwards': isUpwards,
      'resolve-popup--backwards': isBackwards,
    },
    className,
  );
  const portalCalsses = classNames('resolve-popup__portal', portalClassName);
  const contentClasses = classNames(
    'resolve-popup__content',
    {
      'resolve-popup__content--open': isOpen,
      'resolve-popup__content--upwards': isUpwards,
      'resolve-popup__content--backwards': isBackwards,
    },
    contentClassName,
  );
  const triggerClasses = classNames(
    'resolve-popup__trigger',
    {
      'resolve-popup__trigger--open': isOpen,
      'resolve-popup__trigger--upwards': isUpwards,
      'resolve-popup__trigger--backwards': isBackwards,
    },
    contentClassName,
  );

  const content = useMemo(
    () => (
      <>
        {isOpen && (
          <Overlay
            open
            elevate
            parentEl={document.body}
            noContent
            background="transparent"
            className="resolve-popup__overlay"
          />
        )}
        <div className={contentClasses} ref={contentRef}>
          <div className="resolve-popup__content__content-wrapper">
            <div className="resolve-popup__content__content-wrapper__container">
              {popupContent}
            </div>
          </div>
        </div>
      </>
    ),
    [isOpen, contentClasses, popupContent],
  );

  useEffect(() => {
    if (!onHover) {
      setIsOpen(open);
    }
  }, [onHover, open]);

  return (
    <div
      className={classes}
      style={style}
      onMouseEnter={() => {
        if (!onHover) return;

        setIsOpen(true);
        customOnHover?.();
      }}
      onMouseLeave={() => onHover && setIsOpen(false)}
      ref={(r) => {
        popupRef.current = r;
        if (setRef) setRef.current = r;
      }}
    >
      <Portal className={portalCalsses} isOpen={isOpen}>
        {content}
      </Portal>
      <div className={triggerClasses} ref={triggerRef}>
        {children}
      </div>
    </div>
  );
};

export default ResolvePopup;
